import React, { useState, useRef, useEffect } from "react";
import { Card, Row, Modal, Button } from "react-bootstrap";
import DatePicker, { DateObject } from "react-multi-date-picker";
import DatePanel from "react-multi-date-picker/plugins/date_panel"
import { Calendar } from "react-multi-date-picker";
import "react-multi-date-picker/styles/colors/green.css"
import "react-multi-date-picker/styles/layouts/mobile.css";
import { SlArrowDown } from "react-icons/sl";
import './index.scss';
import './DateRangeSelector.scss';

interface DateRangeSelectorProps {
  onDateRangeChange?: (dateRange: string, isCustom?: boolean) => void;
  selectedOption?: DateRangeOption;
}

type DateRangeOption = "Last 30 days" | "Last 90 days" | "Year to date" | "Custom";

// Helper function to get initial dates (moved outside component to avoid recalculation)
const getInitialDates = (option: DateRangeOption): DateObject[] => {
  const today = new DateObject();
  switch (option) {
    case "Last 30 days":
      return [new DateObject().subtract(30, "days"), today];
    case "Last 90 days":
      return [new DateObject().subtract(90, "days"), today];
    case "Year to date":
      return [new DateObject().set({ day: 1, month: 1 }), today];
    case "Custom":
      // For custom, use last 90 days as default until user selects
      return [new DateObject().subtract(90, "days"), today];
    default:
      return [new DateObject().subtract(90, "days"), today];
  }
};

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({ onDateRangeChange, selectedOption: propSelectedOption }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [showCustomModal, setShowCustomModal] = useState(false);

  // Set default to Last 90 days or use prop value
  const [selectedOption, setSelectedOption] = useState<DateRangeOption>(propSelectedOption || "Last 90 days");
  const customModeRef = useRef(false);

  // Main date value for the selector - only calculate once on mount
  const [dateValue, setDateValue] = useState<DateObject[]>(() => 
    getInitialDates(propSelectedOption || "Last 90 days")
  );
  // Temporary value for custom modal (empty array means not open/no selection)
  const [customDateValue, setCustomDateValue] = useState<DateObject[]>([]);



  const dropdownRef = useRef<HTMLDivElement>(null);

  // Original label logic
  const formatDateRange = (dates: DateObject[]): string => {
    if (Array.isArray(dates) && dates.length === 2 && dates[0] && dates[1]) {
      const startDate = dates[0].format("MM/DD/YYYY");
      const endDate = dates[1].format("MM/DD/YYYY");
      return `${startDate} - ${endDate}`;
    }
    return "";
  };

  // Get display label based on selected option
  const getDisplayLabel = (): string => {
    if (selectedOption === "Custom") {
      return formatDateRange(dateValue);
    }
    return selectedOption;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Always start with Last 90 days as default unless explicitly overridden by props
    if (!propSelectedOption || propSelectedOption === "Last 90 days") {
      // Clear any stored custom data to ensure we start fresh
      localStorage.removeItem('dateRangeSelector_customDates');
      customModeRef.current = false;
      setSelectedOption("Last 90 days");
      const defaultDates = getInitialDates("Last 90 days");
      setDateValue(defaultDates);
      return;
    }
    
    // Check if we have stored custom dates only if prop allows it
    const storedCustomData = localStorage.getItem('dateRangeSelector_customDates');
    if (storedCustomData && propSelectedOption === "Custom") {
      try {
        const customData = JSON.parse(storedCustomData);
        if (customData.isCustom) {
          // Parse stored dates properly
          const startDate = new DateObject(new Date(customData.startDate));
          const endDate = new DateObject(new Date(customData.endDate));
          setDateValue([startDate, endDate]);
          setSelectedOption("Custom");
          customModeRef.current = true;
          return; // Skip normal prop handling
        }
      } catch (e) {
        // console.log("Error parsing stored custom data:", e);
        // Clear invalid stored data
        localStorage.removeItem('dateRangeSelector_customDates');
      }
    }
    
    // ABSOLUTE RULE: NEVER update if we're in locked custom mode
    if (customModeRef.current) {
      return;
    }
    
    // Only update for initial load or when switching between preset options
    if (propSelectedOption && propSelectedOption !== selectedOption && propSelectedOption !== "Custom") {
      setSelectedOption(propSelectedOption);
      const newDates = getInitialDates(propSelectedOption);
      setDateValue(newDates);
      if (onDateRangeChange) {
        onDateRangeChange(formatDateRange(newDates), false);
      }
    }
  }, [propSelectedOption]);

  const handleOptionSelect = (option: DateRangeOption): void => {
    let start: DateObject, end: DateObject;
    const today = new DateObject();

    switch (option) {
      case "Last 30 days":
        start = new DateObject().subtract(30, "days");
        end = today;
        break;
      case "Last 90 days":
        start = new DateObject().subtract(90, "days");
        end = today;
        break;
      case "Year to date":
        start = new DateObject().set({ day: 1, month: 1 });
        end = today;
        break;
      default:
        return;
    }

    setDateValue([start, end]);
    setCustomDateValue([]); // Clear custom value for preset options
    setSelectedOption(option);
    customModeRef.current = false; // Exit custom mode for preset options
    localStorage.removeItem('dateRangeSelector_customDates'); // Clear stored custom dates
    setIsOpen(false);

    if (onDateRangeChange) {
      onDateRangeChange(formatDateRange([start, end]), false);
    }
  };

  // Only update the main value and label after confirming
  const handleDateConfirm = (): void => {
    if (Array.isArray(customDateValue) && customDateValue.length === 2) {
      // Create completely new DateObject instances
      const newStartDate = new DateObject(customDateValue[0].toDate());
      const newEndDate = new DateObject(customDateValue[1].toDate());
      
      
      // Update state immediately
      setDateValue([newStartDate, newEndDate]);
      setSelectedOption("Custom");
      customModeRef.current = true; // Lock in custom mode
      
      // Close modal and clear temporary state
      setShowCustomModal(false);
      setCustomDateValue([]);
      
      // Store in localStorage to survive parent component resets
      localStorage.setItem('dateRangeSelector_customDates', JSON.stringify({
        startDate: newStartDate.toDate().toISOString(),
        endDate: newEndDate.toDate().toISOString(),
        isCustom: true
      }));
      
      // Notify parent with custom flag so it can update analysis without overriding selection
      if (onDateRangeChange) {
        onDateRangeChange(formatDateRange([newStartDate, newEndDate]), true);
      }
    } else {
      // If no valid dates selected, just close the modal
      setShowCustomModal(false);
      setCustomDateValue([]);
    }
  };

  const IconCalendar = require('../images/icon-calendar.png');

  return (
    <Card className="h-100 border-0 shadow-sm mb-5 date-picker">
      <Card.Body className="w-auto">
        <div className="d-flex justify-content-end">
          <div ref={dropdownRef} className="position-relative">
            <div
              className="date-container border rounded p-2 text-left d-flex align-items-center cursor-pointer position-relative"
              onClick={() => setIsOpen(!isOpen)}
              style={{ cursor: "pointer", width: "400px", }}
            >
              <img src={IconCalendar} alt="icon" className="me-2" />
              <span>{getDisplayLabel()}</span>
              <SlArrowDown
                style={{
                  position: 'absolute',
                  right: 10,
                }}
              />
            </div>

            {isOpen && (
              <div className="date-block position-absolute mt-1 shadow-sm border rounded bg-white" style={{ width: "400px", zIndex: 1000, right: 0 }}>
                <div className="p-2 d-flex gap-2" style={{ flexWrap: 'wrap' }}>
                  <div
                    className={`p-3 mb-0 rounded text-center border gilroy-semibold mb-1 date-option-btn ${selectedOption === "Last 30 days" ? "active" : ""}`}
                    onClick={() => handleOptionSelect("Last 30 days")}
                    style={{ cursor: "pointer", flexGrow: 1 }}
                  >
                    Last 30 days
                  </div>
                  <div
                    className={`p-3 mb-0 rounded text-center border gilroy-semibold mb-1 date-option-btn ${selectedOption === "Last 90 days" ? "active" : ""}`}
                    onClick={() => handleOptionSelect("Last 90 days")}
                    style={{ cursor: "pointer", flexGrow: 1 }}
                  >
                    Last 90 days
                  </div>
                  <div
                    className={`p-3 mb-0 rounded text-center border gilroy-semibold mb-1 date-option-btn ${selectedOption === "Year to date" ? "active" : ""}`}
                    onClick={() => handleOptionSelect("Year to date")}
                    style={{ cursor: "pointer", flexGrow: 1 }}
                  >
                    Year to date
                  </div>
                  <div
                    className={`w-50 p-3 mb-0 rounded text-center border gilroy-semibold mb-1 date-option-btn ${selectedOption === "Custom" ? "active" : ""}`}
                    style={{ cursor: "pointer" }}
                  onClick={() => {
                    // Start fresh with no pre-selected dates
                    setCustomDateValue([]);
                    setShowCustomModal(true);
                  }}
                  >
                    Custom
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card.Body>

      <Modal
        show={showCustomModal}
        onHide={() => {
          setShowCustomModal(false);
          setCustomDateValue([]);
        }}
        centered
        dialogClassName="custom-date-picker"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Select Date Range</Modal.Title>
        </Modal.Header>

        <Modal.Body className="mx-auto">
          <div className="position-relative">
            <Calendar
              value={customDateValue.length > 0 ? customDateValue : undefined}
              onChange={(dates) => {
                const newDates = Array.isArray(dates) ? dates : [];
                setCustomDateValue(newDates);
              }}
              range
              rangeHover
              format="MM/DD/YYYY"
              plugins={[<DatePanel />]}
              minDate={new DateObject().subtract(10, "years")}
              maxDate={new DateObject().add(5, "years")}
              numberOfMonths={1}
              showOtherDays
            />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="primary"
            onClick={handleDateConfirm}
            className="gilroy-semibold font-16 text-white"
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default DateRangeSelector;
